import { supabase } from '../lib/supabase';

export const superAdminPlayerService = {
  /**
   * Search players with filters (super admin only)
   */
  async searchPlayers(filters = {}, pagination = { page: 1, pageSize: 20 }) {
    const { data, error } = await supabase.rpc('search_players_super_admin', {
      search_term: filters.searchTerm || null,
      city_filter: filters.city || null,
      age_category_filter: filters.ageCategory || null,
      school_id_filter: filters.schoolId || null,
      page_num: pagination.page || 1,
      page_size: pagination.pageSize || 20
    });

    if (error) throw error;
    return typeof data === 'string' ? JSON.parse(data) : data;
  },

  /**
   * Get player details with full information (super admin only)
   */
  async getPlayerDetails(playerId) {
    const { data, error } = await supabase.rpc('get_player_details_super_admin', {
      player_id: playerId
    });

    if (error) throw error;
    return typeof data === 'string' ? JSON.parse(data) : data;
  },

  /**
   * Update player (super admin only) - does NOT affect registration status
   */
  async updatePlayer(playerId, updates) {
    const { data, error } = await supabase.rpc('update_player_super_admin', {
      player_id: playerId,
      player_updates: {
        firstName: updates.firstName,
        middleName: updates.middleName || null,
        lastName: updates.lastName,
        dateOfBirth: updates.dateOfBirth,
        mobileNumber: updates.mobileNumber,
        email: updates.email,
        mcaId: updates.mcaId || null,
        role: updates.role,
        tshirtSize: updates.tshirtSize,
        pantSize: updates.pantSize,
        isCaptain: updates.isCaptain || false
      }
    });

    if (error) throw error;
    return typeof data === 'string' ? JSON.parse(data) : data;
  },

  /**
   * Upload/replace player document (super admin only)
   */
  async uploadPlayerDocument(playerId, documentType, file) {
    const fileExt = file.name.split('.').pop();
    const fileName = `${playerId}/${documentType}_${Date.now()}.${fileExt}`;
    const filePath = `player-documents/${fileName}`;

    // Upload to storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('player-documents')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true // Replace if exists
      });

    if (uploadError) throw uploadError;

    const fileSize = file.size;

    // Upsert document record
    const { data: docData, error: docError } = await supabase
      .from('player_documents')
      .upsert({
        player_id: playerId,
        document_type: documentType,
        file_path: filePath,
        file_size: fileSize
      }, {
        onConflict: 'player_id,document_type'
      })
      .select()
      .single();

    if (docError) throw docError;

    return docData;
  },

  /**
   * Delete player document (super admin only)
   */
  async deletePlayerDocument(playerId, documentType) {
    // Get document to find file path
    const { data: doc, error: fetchError } = await supabase
      .from('player_documents')
      .select('file_path')
      .eq('player_id', playerId)
      .eq('document_type', documentType)
      .single();

    if (fetchError) throw fetchError;

    // Delete from storage
    const fileName = doc.file_path.replace('player-documents/', '');
    const { error: storageError } = await supabase.storage
      .from('player-documents')
      .remove([fileName]);

    if (storageError) throw storageError;

    // Delete from database
    const { error: deleteError } = await supabase
      .from('player_documents')
      .delete()
      .eq('player_id', playerId)
      .eq('document_type', documentType);

    if (deleteError) throw deleteError;

    return true;
  }
};

